import { useState, useCallback } from "react";
import { uploadOperationsFromFile } from "../services/operations/operationsUploadService";
import { UploadResult } from "../types/operations";

// =============================================
// State shape returned by this hook.
// =============================================
export interface UseUploadOperationsResult {
  uploadResult: UploadResult | null;
  isUploading: boolean;
  uploadError: string | null;
  uploadFile: (file: File) => Promise<void>;
  clearUploadResult: () => void;
}

// =============================================
// Manages the async state for uploading a
// single Excel file to Supabase.
//
// uploadFile triggers the full upload pipeline:
//   parse → resolve → conflict check → insert.
//
// On completion, uploadResult holds the summary.
// On failure, uploadError holds the message.
// isUploading is true while the pipeline runs.
//
// clearUploadResult allows the caller to dismiss
// the upload result banner before the next upload.
// =============================================
export function useUploadOperations(): UseUploadOperationsResult {
  const [uploadResult, setUploadResult] = useState<UploadResult | null>(null);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const uploadFile = useCallback(async (file: File): Promise<void> => {
    setIsUploading(true);
    setUploadResult(null);
    setUploadError(null);

    try {
      const result = await uploadOperationsFromFile(file);
      setUploadResult(result);
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "An unexpected error occurred during the file upload.";
      setUploadError(message);
    } finally {
      setIsUploading(false);
    }
  }, []);

  const clearUploadResult = useCallback(() => {
    setUploadResult(null);
    setUploadError(null);
  }, []);

  return { uploadResult, isUploading, uploadError, uploadFile, clearUploadResult };
}
